/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useEffect, useState } from 'react';
import { Button } from '@marriott/mi-ui-library';
import { PageParamsContext } from '../../../../modules/context/PageContext';
import {
  AEMInternetBarProps,
  InternetBarMarketingExperienceOneProps,
  InternetBarStateTypes,
} from '../../InternetBar.types';
import { inStayMockResponse } from '../../__mock__/InternetBar.model';
import { INTERNET_BAR_INITIAL_STATE } from '../../utils/constant';
import {
  internetBarIcons,
  globalDivisionList,
  analyticsTrackingIdentifier,
  getInternetBarTrackingIdentifier,
} from '../../../../modules/utils/constants/constants';
import { sanitize } from '../../../../modules/utils/common';
import { useRouter } from 'next/router';
import { connectBtnTrigger, getEnrolledUserFlag } from '../../utils/handler';
import { useSession } from '@marriott/mi-headless-utils';
import { serviceStore, useServiceStore } from '../../../../modules/store/serviceStore';

export const InternetBarMarketingExperienceOne: FC<InternetBarMarketingExperienceOneProps> = (
  pageProps: InternetBarMarketingExperienceOneProps
) => {
  const { preProcessorResponse } = useContext(PageParamsContext);

  const { state = '', userState, propertyInfo } = preProcessorResponse || {};
  const globalDivision = propertyInfo?.data?.basicInformation?.globalDivision;

  const {
    currentStateMapping = {
      ...(pageProps?.isAuthorMode ? inStayMockResponse : (INTERNET_BAR_INITIAL_STATE as InternetBarStateTypes)),
    },
  } = pageProps;
  const { datalayer } = useSession();
  const router = useRouter();
  const urlQueryParams: Record<string, any> = router.query;

  const serviceData = useServiceStore((state: serviceStore) => state.serviceData);

  const [isNoLoginInterfaceModal, setIsNoLoginInterfaceModal] = useState<boolean>(
    currentStateMapping?.noLoginInterfaceModal ?? false
  );

  const [yesButtonClickTrack, setYesButtonClickTrack] = useState<string>('');
  const [noButtonClickTrack, setNoButtonClickTrack] = useState<string>('');

  const [showIcon] = useState<boolean>(
    internetBarIcons && internetBarIcons?.[state] ? internetBarIcons?.[state]?.showIcon : false
  );

  const termsAndConditionsText = sanitize(
    (
      currentStateMapping?.termsAndConditions &&
      pageProps?.[currentStateMapping?.termsAndConditions as keyof AEMInternetBarProps]
    )?.toString() || ''
  );

  useEffect(() => {
    const { Z = '', zoneMapping } = pageProps?.internetBarCurrentStateData ?? {};
    const propertyPricingTier = zoneMapping?.[Z ?? 'GUEST'];
    let yesBtnClickTrack = '';
    let noBtnClickTrack = '';
    let propertyType = '';
    if (pageProps && pageProps?.internetPricingData && pageProps?.internetPricingData?.data) {
      const { uxlGlobalDivision, enrollmentSourceCode } = pageProps?.internetPricingData?.data ?? {};
      if (datalayer?.['prop_brand_tier']) {
        propertyType = datalayer?.['prop_brand_tier'] as string;
      }
      yesBtnClickTrack = `${getInternetBarTrackingIdentifier(
        state,
        analyticsTrackingIdentifier.internetBar,
        propertyPricingTier
      )}|${uxlGlobalDivision
        ?.toString()
        ?.replaceAll(' ', '')};${propertyType};${userState};${enrollmentSourceCode};Yes|internal`;
      noBtnClickTrack = `${getInternetBarTrackingIdentifier(
        state,
        analyticsTrackingIdentifier.internetBar,
        propertyPricingTier
      )}|${uxlGlobalDivision?.toString()?.replaceAll(' ', '')};${enrollmentSourceCode};No|internal`;
    } else {
      yesBtnClickTrack = `${getInternetBarTrackingIdentifier(
        state,
        analyticsTrackingIdentifier.internetBar,
        propertyPricingTier
      )}|Yes|internal`;
      noBtnClickTrack = `${getInternetBarTrackingIdentifier(
        state,
        analyticsTrackingIdentifier.internetBar,
        propertyPricingTier
      )}|No|internal`;
    }
    setYesButtonClickTrack(yesBtnClickTrack);
    setNoButtonClickTrack(noBtnClickTrack);
  }, [pageProps, pageProps?.internetPricingData, pageProps?.internetBarCurrentStateData, userState, datalayer]);

  const handleBtnClick = (btnName: string) => {
    const { setCurrentStateHasModalOption, setModalType, setShowInternetBarModal, internetPricingData } = pageProps;
    const internetbarInitialValue = { ...pageProps?.internetBarCurrentStateData };
    const zoneValue = internetbarInitialValue['Z'] ?? '';
    const localRequirement = internetbarInitialValue['LR']?.toUpperCase() ?? '';
    const ppv5Enrollment =
      internetbarInitialValue['ppv5Enrollment'] ?? urlQueryParams?.['ppv5Enrollment']?.toLowerCase() === 'true';
    const loyaltyEnrollmentSourceCode = internetPricingData?.['data']?.enrollmentSourceCode
      ? internetPricingData?.['data']?.enrollmentSourceCode
      : internetbarInitialValue['loyaltyEnrollmentSourceCode'];
    const isUserEnrolled: boolean = getEnrolledUserFlag(loyaltyEnrollmentSourceCode);

    switch (btnName) {
      case 'connectnow':
        if (zoneValue?.toUpperCase() === 'GUEST') {
          const { Z = '', zoneMapping } = pageProps?.internetBarCurrentStateData || {};
          if (
            globalDivision &&
            globalDivision === globalDivisionList?.APEC &&
            !localRequirement &&
            zoneMapping[Z ?? ''] === 'SF'
          ) {
            connectBtnTrigger(true, { ...internetbarInitialValue });
          } else {
            setShowInternetBarModal(true);
            setModalType('LOGIN');
            setCurrentStateHasModalOption(true);
          }
          if (localRequirement === 'STRICT_AO') {
            setModalType('ACCESS');
          } else if (isNoLoginInterfaceModal) {
            connectBtnTrigger(isNoLoginInterfaceModal, { ...internetbarInitialValue });
          }
        } else if (zoneValue?.toUpperCase() === 'PUBLIC' || zoneValue?.toUpperCase() === 'LOBBY') {
          if (isUserEnrolled && ppv5Enrollment) {
            setShowInternetBarModal(true);
            setModalType('LOYALTY_WELCOME');
            setCurrentStateHasModalOption(true);
            setIsNoLoginInterfaceModal(false);
          } else {
            if (localRequirement && localRequirement !== 'TCTC') {
              if (localRequirement === 'AF_AO' || localRequirement === 'STRICT_AO') {
                setModalType('ACCESS');
                setShowInternetBarModal(true);
                setCurrentStateHasModalOption(true);
              } else {
                setModalType('LOGIN');
                setShowInternetBarModal(true);
                connectBtnTrigger(isNoLoginInterfaceModal, { ...internetbarInitialValue });
              }
            } else {
              setModalType('');
              setShowInternetBarModal(false);
              connectBtnTrigger(true, { ...internetbarInitialValue });
            }
          }
        }
        break;
    }
  };

  useEffect(() => {
    if (serviceData?.connectBtnClick) {
      handleBtnClick('connectnow');
    }
  }, [serviceData?.connectBtnClick]);

  return currentStateMapping && pageProps ? (
    <div className={'col-12 d-flex flex-column justify-content-center px-0 text-center align-items-center'}>
      {/* 1. Internet Bar Icon Configuration */}
      {showIcon && (
        <span className={`t-accent-color d-inline-block ${internetBarIcons?.[state]?.iconName}  icon-xxl`}></span>
      )}
      {/* 2. Title */}
      <h3 className={`col-12 t-subtitle-xl my-4 px-5 px-md-3`}>{pageProps?.newAgeExp ?? ''}</h3>

      <div className={'loyalty-container'}>
        <div className={'d-flex flex-column'}>
          <p className={'t-font-s loyalty-description-title mb-0'}>{pageProps?.isMBVMemberLabel}</p>
          <p className={'t-font-s loyalty-description mb-0'}>{pageProps?.isMBVMemberDesc}</p>
        </div>
        <div className={'d-flex flex-column flex-md-row justify-content-center align-items-center'}>
          <Button
            className={'m-button-primary mt-4 d-flex justify-content-center align-items-center loyalty-buttons mr-md-3'}
            isLink={false}
            trackingProperties={pageProps?.trackingProperties}
            custom_click_track_value={noButtonClickTrack}
            data-custom_click_track_value={noButtonClickTrack}
            data-is-click-track={true}
            ariaLabel={pageProps?.isMBVMemberNoLabel}
            callback={() => pageProps?.setViewLoyaltyScreen(true)}
          >
            <span>{pageProps?.isMBVMemberNoLabel}</span>
          </Button>
          <Button
            className={
              'm-button-secondary mt-4 d-flex justify-content-center align-items-center loyalty-buttons ml-md-3'
            }
            isLink={false}
            trackingProperties={pageProps?.trackingProperties}
            custom_click_track_value={yesButtonClickTrack}
            data-custom_click_track_value={yesButtonClickTrack}
            data-is-click-track={true}
            ariaLabel={pageProps?.isMBVMemberYesLabel}
            callback={() => handleBtnClick('connectnow')}
          >
            <span>{pageProps?.isMBVMemberYesLabel}</span>
          </Button>
        </div>
      </div>
      <div className="w-100">
        {/* Separator */}
        <div className={`t-accent-color mt-4 internetbar-marketing-content__separator`}></div>

        {currentStateMapping?.termsAndConditions ? (
          <p
            className={`t-font-xs internetbar-marketing-content__footer-container__tnc-text`}
            dangerouslySetInnerHTML={{
              __html: termsAndConditionsText,
            }}
          ></p>
        ) : null}
      </div>
    </div>
  ) : null;
};
